<template>
  <button
      type="button"
      class="clinic-detail-fab"
      :aria-label="ariaLabel"
      @click="emit('navigate')"
  >
    <SvgoChevronDown aria-hidden="true" class="clinic-detail-fab__caret"/>
    <span class="clinic-detail-fab__body">
      <span class="clinic-detail-fab__title">{{ title }}</span>
      <span v-if="detail" class="clinic-detail-fab__detail">{{ detail }}</span>
    </span>
  </button>
</template>

<script lang="ts" setup>
import type {VeterinarianBookingNearestSlot} from '~/types/veterinar';
import {formatNearestSlotWhen} from '~/utils/nearestSlotLabel';

const props = defineProps<{
  nearestSlot?: VeterinarianBookingNearestSlot | null;
}>();

const emit = defineEmits<{
  navigate: [];
}>();

const hasSlot = computed(() => {
  const slot = props.nearestSlot;
  return Boolean(slot?.date?.trim() && slot?.time?.trim());
});

const title = computed(() => hasSlot.value ? 'Ближайший приём' : 'Записаться');
const detail = computed(() => {
  if (!hasSlot.value || !props.nearestSlot) {
    return '';
  }
  return formatNearestSlotWhen(props.nearestSlot.date, props.nearestSlot.time);
});
const ariaLabel = computed(() => hasSlot.value ? 'Перейти к врачам клиники' : 'Перейти к записи');
</script>

<style lang="scss">
.clinic-detail-fab {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  max-width: calc(100vw - 32px);
  padding: 12px 20px 12px 12px;
  margin: 0;
  border: none;
  border-radius: 16px;
  background-color: $color-pink;
  box-shadow: $shadow3-box-shadow;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
  appearance: none;
  -webkit-tap-highlight-color: transparent;

  &__caret {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: $color-accent-text;
    transform: rotate(180deg);
  }

  &__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    min-width: 0;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;
    color: $color-accent-text;
    white-space: nowrap;
  }

  &__detail {
    font-size: 14px;
    line-height: 1.35;
    color: $color-black;
    white-space: nowrap;
  }

  &:focus-visible {
    outline: 2px solid $color-purple;
    outline-offset: 2px;
  }
}

.clinic-detail-fab-anchor {
  position: fixed;
  left: 16px;
  bottom: calc(16px + env(safe-area-inset-bottom));
  z-index: 40;
}
</style>
